import { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, Circle, Loader2, Send as SendIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const THEMES = {
  paper: {
    card: '#FFFFFF', border: '#E7E0D3', ink: '#2B2A26', soft: '#6B6558',
    done: '#0D9488', doneBg: '#CCFBF1', pending: '#B9B3A6',
    bar: '#0D9488', barBg: '#EDE7DA', sentFg: '#065F46', sentBg: '#D1FAE5',
    rowBorder: '#F0EBE0',
  },
  dark: {
    card: '#2A3240', border: 'rgba(255,255,255,0.1)', ink: '#FFFFFF', soft: '#9AA4B2',
    done: '#34D399', doneBg: 'rgba(16,185,129,0.15)', pending: '#5B6472',
    bar: '#34D399', barBg: 'rgba(255,255,255,0.08)', sentFg: '#6EE7B7', sentBg: 'rgba(16,185,129,0.15)',
    rowBorder: 'rgba(255,255,255,0.06)',
  },
};

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); }
  catch { return (iso || '').slice(0, 10); }
}

/**
 * Building Control stage checklist for a single project (BC-1 Stage Tracker).
 * variant: 'paper' (Site Diary) or 'dark' (admin Settings).
 */
export function BCStageTracker({ projectId, variant = 'paper', refreshKey = 0 }) {
  const { api } = useAuth();
  const T = THEMES[variant] || THEMES.paper;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try { const r = await api.get(`/bc/projects/${projectId}/tracker`); setData(r.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [api, projectId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: T.soft }} /></div>;
  if (!data) return <p className="text-sm py-4" style={{ color: T.soft }}>No progress data.</p>;

  const pct = data.total_standard ? Math.round((data.captured_standard / data.total_standard) * 100) : 0;

  return (
    <div data-testid="bc-stage-tracker">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[13px] font-semibold" style={{ color: T.ink }}>
          {data.captured_standard} of {data.total_standard} stages captured
        </span>
        <span className="text-[12px] font-bold" style={{ color: T.done }}>{pct}%</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: T.barBg }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: T.bar }} />
      </div>
      <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${T.border}`, background: T.card }}>
        {data.stages.map((s, i) => (
          <div key={s.stage} className="flex items-center gap-2.5 px-3 py-2.5"
            style={{ borderTop: i === 0 ? 'none' : `1px solid ${T.rowBorder}` }}
            data-testid={`bc-track-${s.captured ? 'done' : 'todo'}`}>
            {s.captured
              ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: T.done }} />
              : <Circle className="w-4 h-4 flex-shrink-0" style={{ color: T.pending }} />}
            <span className="text-[13px] flex-1 min-w-0 truncate" style={{ color: s.captured ? T.ink : T.soft, fontWeight: s.captured ? 600 : 400 }}>
              {s.stage}{s.custom ? ' *' : ''}
            </span>
            {s.captured && (
              <span className="text-[11px] flex items-center gap-2" style={{ color: T.soft }}>
                {s.count > 1 && <span>{s.count}×</span>}
                <span>{fmt(s.last_captured_at)}</span>
                {s.sent && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold flex items-center gap-1" style={{ background: T.sentBg, color: T.sentFg }}>
                    <SendIcon className="w-2.5 h-2.5" /> Sent
                  </span>
                )}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default BCStageTracker;
